// build: 2026-02-19 cache-bust for production chunk
import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShieldCheck, BookOpen, MessagesSquare, Linkedin } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Independence", body: "We accept no paid placements and no platform allegiances. Editorial standards are public and enforced." },
  { icon: BookOpen, title: "Expertise", body: "Our contributors include clinicians, regulators, model researchers, and operators—not pundits." },
  { icon: Globe, title: "Global", body: "We track AI policy and practice across more than thirty jurisdictions, with intentional coverage outside the OECD axis." },
  { icon: MessagesSquare, title: "Multi-perspective", body: "We present disagreement on the page rather than smoothing it over. Plurality is a feature, not a bug." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-28">
          <p className="overline">About</p>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-bold tracking-tight leading-[0.98] max-w-5xl">
            Ethics in AI is not a stance. It is a discipline. We treat it like one.
          </h1>
          <p className="mt-10 max-w-3xl font-display italic text-2xl lg:text-3xl text-[var(--eai-ink-2)] leading-[1.4]">
            “We are an independent editorial and research practice translating ethics from principle into procedure — for the people building, governing, and regulating AI.”
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="overline">Mission</p>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-[var(--eai-ink-2)] leading-relaxed">
            <p>The conversation about ethical AI has plenty of principles and not enough procedure. Ethical AI Now exists to close that gap — to give people who decide, build, and oversee AI systems something defensible to ship on Monday.</p>
            <p>We publish weekly curated intelligence, working frameworks, and sector-specific guides. We convene practitioners in roundtables, webinars, and workshops. And we do all of it with the same standards we expect of the systems we cover: traceable, testable, and accountable.</p>
            <p>We are rigorous, but accessible. Evidence-informed, not evidence-bullying. Multi-perspective by design.</p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="border-b border-[var(--eai-border)]" data-testid="founder-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline">Founder</p>
            <div className="mt-6 w-full max-w-md border border-[var(--eai-ink)] bg-[var(--eai-ink)] text-white relative overflow-hidden flex flex-col">
              <div className="aspect-[4/5] relative bg-[var(--eai-ink)]">
                <img
                  src="https://customer-assets.emergentagent.com/wingman/8b845ce7-5d58-480c-9d7c-7850e94166e2/attachments/1e0b231c6bfb4d719092575079f7837b_image.bin"
                  alt="Matthew Kilkenny, Founder of Ethical AI Now"
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid="founder-portrait-about"
                />
                <div className="absolute top-6 left-6 right-6 flex items-start justify-between z-10">
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">Portrait No. 01</p>
                  <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/70 bg-[var(--eai-ink)]/60 px-2 py-0.5">MK · 2026</p>
                </div>
              </div>
              <div className="border-t border-white/20 p-5">
                <p className="font-display text-2xl font-semibold leading-tight">Matthew Kilkenny</p>
                <p className="mt-1 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-white/70">AI Ethics Ambassador<br/>&amp; Strategic Policy Advisor</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              A practitioner-led practice — built on the conviction that the future of AI must be anchored in <em className="font-display">human dignity</em>.
            </h2>
            <div className="mt-8 space-y-5 text-lg text-[var(--eai-ink-2)] leading-relaxed max-w-2xl">
              <p>
                <span className="text-[var(--eai-ink)] font-medium">Matthew Kilkenny</span> is a leading voice in AI ethics, the founder of Ethical AI Now, and a key contributor to Ireland&apos;s national readiness for the EU AI Act.
              </p>
              <p>
                With a deep background in the humanities, psychology, and theology, he serves as a strategic thought leader bridging complex AI architecture and the ethical governance needed for a responsible digital future.
              </p>
              <p>
                He authored the strategic brief: <Link to="/intelligence/ireland-ai-readiness" className="text-[var(--eai-ink)] underline decoration-[var(--eai-accent)] decoration-2 underline-offset-4 hover:text-[var(--eai-accent)] transition-colors" data-testid="about-founder-brief-link">“Ireland&apos;s AI Readiness: A Strategic Brief on Ethics, Innovation &amp; Global Positioning.”</Link>
              </p>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl border-t border-[var(--eai-border)] pt-8">
              {[
                ["Humanities", "A tradition of asking what technology is for"],
                ["Psychology", "How systems shape — and are shaped by — people"],
                ["Theology", "First principles for human dignity"],
              ].map(([n, l]) => (
                <div key={n}>
                  <p className="font-display text-2xl lg:text-3xl font-semibold leading-none tracking-tight">{n}</p>
                  <p className="mt-2 text-[11px] font-mono-eai uppercase tracking-[0.18em] text-[var(--eai-ink-2)] leading-snug">{l}</p>
                </div>
              ))}
            </div>

            <figure className="mt-12 border-l-2 border-[var(--eai-accent)] pl-6">
              <blockquote className="font-display italic text-2xl lg:text-3xl leading-[1.35] text-[var(--eai-ink)]">
                “The future of technology has to be anchored in something older and steadier than technology itself — in human dignity.”
              </blockquote>
              <figcaption className="mt-4 font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">
                Matthew Kilkenny — Founder
              </figcaption>
            </figure>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="eai-btn" data-testid="founder-contact-cta">
                Write to Matthew <ArrowRight size={16} />
              </Link>
              <a
                href="https://www.linkedin.com/in/ethical-ai-now/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border border-[var(--eai-ink)] text-[var(--eai-ink)] hover:bg-[var(--eai-ink)] hover:text-white transition-colors duration-200"
                data-testid="founder-linkedin-cta"
              >
                <Linkedin size={16} strokeWidth={1.8} /> Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Magnifica Humanitas — Call to Rome */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-ink)] text-white" data-testid="magnifica-humanitas-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <figure className="aspect-[4/5] w-full overflow-hidden border border-white/20">
              <img
                src="https://customer-assets.emergentagent.com/wingman/8b845ce7-5d58-480c-9d7c-7850e94166e2/attachments/406beb975174470c96a775c3bf850067_image.bin"
                alt="Matthew Kilkenny — Magnifica Humanitas"
                className="w-full h-full object-cover"
                data-testid="magnifica-humanitas-image"
              />
            </figure>
          </div>
          <div className="lg:col-span-6">
            <p className="overline text-[var(--eai-accent)]">Magnifica Humanitas</p>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              A Call to Rome <span className="text-white/60 italic">for AI Ethics.</span>
            </h2>
            <div className="mt-8 space-y-5 text-lg text-white/75 leading-relaxed max-w-xl">
              <p>
                <span className="text-white font-medium">Magnifica Humanitas</span> is our shorthand for a conviction at the heart of this practice: that the dignity of the human person is not a setting to be tuned but a horizon to be honoured.
              </p>
              <p>
                We stand in alignment with <span className="text-white font-medium">The Call to Rome for AI Ethics</span> — the cross-tradition movement, launched in 2020 at the Pontifical Academy for Life, calling for AI built around transparency, inclusion, responsibility, impartiality, reliability, security and privacy. The conversation has only deepened: from Pope Francis&apos;s address to the G7 in 2024 to Pope Leo XIV&apos;s repeated framing of AI as not merely a technical question but an anthropological one.
              </p>
              <p>
                Our manifesto — <em className="text-white">New Machines. Same Soul.</em> — is written in that same register. Inter-tradition, plural in voice, anchored in conscience.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/manifesto"
                className="inline-flex items-center gap-2 bg-white text-[var(--eai-ink)] px-5 py-3 text-sm font-medium hover:bg-[var(--eai-accent)] hover:text-white transition-colors duration-200"
                data-testid="magnifica-manifesto-cta"
              >
                Read the Manifesto <ArrowRight size={16} />
              </Link>
              <Link to="/intelligence/the-vatican-and-the-mythos-crisis" className="text-sm font-mono-eai uppercase tracking-[0.18em] text-white/70 hover:text-white border-b border-transparent hover:border-white pb-0.5">
                The Vatican &amp; the Mythos Crisis →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4"><p className="overline">What we stand for</p></div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">Four commitments, in writing.</h2>
            </div>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--eai-border)]">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white p-8 lg:p-10">
                <v.icon size={28} strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{v.title}</h3>
                <p className="mt-3 text-sm text-[var(--eai-ink-2)] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="overline">Practitioner&apos;s Footprint</p>
            <h2 className="mt-4 font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">A record, not a roster.</h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--eai-border)]">
            {[
              ["12,000+", "LinkedIn Followers & Policy Peers", "A community of practitioners, policymakers, and parents engaging weekly."],
              ["Strategic Policy Advisor", "Ireland's EU AI Act Readiness", "Contributing to national posture ahead of the 2026 Council Presidency."],
              ["Founder", "Ethical AI Now", "An independent editorial and research practice. Rigorous, accessible, plural."],
              ["Author", "Ireland's AI Readiness Strategic Brief", "Ethics, innovation, and global positioning."],
            ].map(([n, l, sub]) => (
              <div key={l} className="bg-white p-8 lg:p-10">
                <p className="font-display text-3xl lg:text-4xl font-bold leading-tight tracking-tight">{n}</p>
                <p className="mt-3 text-xs font-mono-eai uppercase tracking-[0.18em] text-[var(--eai-accent)]">{l}</p>
                <p className="mt-3 text-sm text-[var(--eai-ink-2)] leading-relaxed">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="overline">Formation &amp; Credentials</p>
            <h2 className="mt-4 font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
              Where the reasoning was formed.
            </h2>
            <p className="mt-5 text-[var(--eai-ink-2)] leading-relaxed max-w-md">
              The practice draws on continuing formation in the humanities, theology, and evangelization &mdash; the disciplines that keep AI ethics from collapsing into pure engineering.
            </p>
          </div>
          <ul className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--eai-border)]" data-testid="about-credentials">
            <li className="bg-white p-8 lg:p-10" data-testid="credential-word-on-fire">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                Word on Fire Institute
              </p>
              <p className="mt-4 font-display text-2xl lg:text-3xl leading-snug font-semibold">
                Evangelization 101
              </p>
              <p className="mt-3 text-sm text-[var(--eai-ink-2)] leading-relaxed">
                Certificate of completion. A grounding in the intellectual tradition, apologetics, and cultural encounter that shapes the &ldquo;Reason Informed by Faith&rdquo; posture of Ethical AI Now.
              </p>
              <p className="mt-6 font-mono-eai text-[10px] uppercase tracking-[0.22em] text-[var(--eai-ink-2)]">
                Bishop Robert Barron &middot; Word on Fire
              </p>
              <p className="mt-3 font-mono-eai text-[10px] tracking-normal normal-case text-[var(--eai-ink-2)]" data-testid="credential-evangelization-line">
                Graduate, Evangelization 101 (Word on Fire Institute, 2026)
              </p>
            </li>
            <li className="bg-white p-8 lg:p-10" data-testid="credential-continuing-formation">
              <p className="font-mono-eai text-[11px] uppercase tracking-[0.22em] text-[var(--eai-accent)]">
                Continuing Formation
              </p>
              <p className="mt-4 font-display text-2xl lg:text-3xl leading-snug font-semibold">
                Humanities &middot; Psychology &middot; Theology
              </p>
              <p className="mt-3 text-sm text-[var(--eai-ink-2)] leading-relaxed">
                A working ground in the disciplines that hold human dignity, cognition, and conscience &mdash; the ballast for policy that has to be liveable, not only lawful.
              </p>
            </li>
          </ul>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="overline">Work with us</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              Briefings, retained advisory, and bespoke evaluations.
            </h2>
            <p className="mt-6 text-[var(--eai-ink-2)] max-w-xl leading-relaxed">
              We work with public agencies, regulated enterprises, and well-aligned platforms. We accept engagements selectively to protect our independence.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right flex lg:justify-end items-end">
            <Link to="/contact" className="eai-btn" data-testid="about-contact-cta">
              Start a conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
